import { type Question } from '@strata/react-question-tree';
import { type ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';

import { CLINICAL_POLICY_API_URL, fmhNpiAPI } from 'api/APIBase';

export async function getQuestionTree({
    payerId,
    productServiceOid,
    diagnosisOid,
}: {
    payerId: string;
    productServiceOid: string;
    diagnosisOid: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/policies/${productServiceOid}/${diagnosisOid}`;
    const data = await fmhNpiAPI({ url, payerId }).get().json<{ graph: string }>();
    return JSON.parse(data.graph) as { nodes: Question[] };
}

export async function putApproveDraft({
    payerId,
    draftId,
    effectiveDate,
    comment,
}: {
    payerId: string;
    draftId: string;
    effectiveDate?: string;
    comment: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/drafts/${draftId}/approve`;
    const body = {
        internal: false,
        effective_date: effectiveDate,
        comment,
    };
    return fmhNpiAPI({ url, payerId }).put(body).json();
}

export async function putRequestChanges({
    payerId,
    draftId,
    change,
    attachments,
}: {
    payerId: string;
    draftId: string;
    change: string;
    attachments?: string[];
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/drafts/${draftId}/changes`;
    const body = {
        internal: false,
        change,
        attachments,
    };
    return fmhNpiAPI({ url, payerId }).put(body).json();
}

export async function putUpdateDraftStatus({
    payerId,
    draftId,
    policyStatus,
    comment,
}: {
    payerId: string;
    draftId: string;
    policyStatus: (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses];
    comment?: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/drafts/${draftId}/next`;
    const body = {
        policy_status: policyStatus,
        comment,
    };
    return fmhNpiAPI({ url, payerId }).put(body).json();
}

/* Change Request Files */

export async function getPolicyFileList({
    payerId,
    draftId,
}: {
    payerId: string;
    draftId: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/attachments/${draftId}`;
    return fmhNpiAPI({ url, payerId }).get().json<{ name: string; size: number }[]>();
}

export async function getPolicyFile({
    payerId,
    draftId,
    filename,
}: {
    payerId: string;
    draftId: string;
    filename: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/attachments/${draftId}/${filename}`;
    return fmhNpiAPI({ url, payerId }).get().blob();
}

export async function postPolicyFile({
    payerId,
    draftId,
    file,
}: {
    payerId: string;
    draftId: string;
    file: File;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/attachments/${draftId}`;
    return fmhNpiAPI({ url, payerId }).formData({ file }).post().json();
}

export async function deletePolicyFile({
    payerId,
    draftId,
    filename,
}: {
    payerId: string;
    draftId: string;
    filename: string;
}) {
    const url = `${CLINICAL_POLICY_API_URL}/${payerId}/attachments/${draftId}/${filename}`;
    return fmhNpiAPI({ url, payerId }).delete().json();
}
